/**
 * Maps cruise itinerary detail into web Trip fields for import.
 * Mirrors iOS CruiseItineraryMapper + CityVisitCountryEnricher (CSV country/ISO2 fill).
 * Catalog arrival/departure stay naive wall-clock (YYYY-MM-DD + HH:mm). Destination
 * IANA `timeZoneIdentifier` is metadata only — never used to convert those times.
 */

import type { Trip, TripCityVisitMeta } from '../types/client';
import { createEmptyTrip } from '../types/client';
import {
  addDaysToIsoDate,
  displayPortName,
  isAtSeaLocation,
  parsePortTimeParts,
  shouldSkipCountryEnrichment,
  type CruiseItineraryDetail,
  type CruisePortStop,
} from './cruiseItinerariesApi';
import { rowForPort } from './cruisePortCityMap';
import { reverseGeocodeCountry } from './countryInfoApi';
import { generatedCruiseTripName } from '../utils/cruiseImportTripName';

export interface CruiseImportMappedTrip {
  tripName: string;
  startDate: string;
  endDate: string;
  cityIds: string[];
  cityNames: Record<string, string>;
  cityCoords: Record<string, { latitude: number; longitude: number }>;
  cityVisitMeta: Record<string, TripCityVisitMeta>;
  portLabels: string[];
  daysBeforeCruise: number;
  daysAfterCruise: number;
  itineraryId: string;
  cruiseLine: string;
  shipName: string;
  cruiseType?: string | null;
}

function clampDays(value: number): number {
  return Math.max(0, Math.min(14, Math.floor(value) || 0));
}

function isValidIanaTimeZone(id: string): boolean {
  if (!id.trim()) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: id });
    return true;
  } catch {
    return false;
  }
}

function coordCacheKey(lat: number, lng: number): string {
  return `${lat.toFixed(3)},${lng.toFixed(3)}`;
}

/**
 * Fill country / ISO2 / timezone from the port CSV (and rare reverse-geocode misses).
 * Mutates cityNames + cityVisitMeta in place. Skips At Sea / cruising / hotel overnight / flight.
 */
export async function enrichMappedPortsWithCountry(args: {
  ports: CruisePortStop[];
  cityIds: string[];
  cityNames: Record<string, string>;
  cityCoords: Record<string, { latitude: number; longitude: number }>;
  cityVisitMeta: Record<string, TripCityVisitMeta>;
  reverseGeocode?: typeof reverseGeocodeCountry;
}): Promise<void> {
  const {
    ports,
    cityIds,
    cityNames,
    cityCoords,
    cityVisitMeta,
    reverseGeocode = reverseGeocodeCountry,
  } = args;
  const geocodeCache = new Map<
    string,
    Awaited<ReturnType<typeof reverseGeocodeCountry>>
  >();

  for (let i = 0; i < cityIds.length; i += 1) {
    const cityId = cityIds[i];
    const stop = ports[i];
    if (!stop) continue;
    const label = cityNames[cityId] || displayPortName(stop.port);
    if (shouldSkipCountryEnrichment(label)) continue;

    const row = rowForPort(stop.port);
    if (row) {
      cityNames[cityId] = row.city;
      const meta = cityVisitMeta[cityId] ?? {};
      meta.countryName = row.country;
      meta.countryISO2 = row.countryISO2;
      if (isValidIanaTimeZone(row.timeZoneIdentifier)) {
        meta.timeZoneIdentifier = row.timeZoneIdentifier;
      }
      cityVisitMeta[cityId] = meta;
      continue;
    }

    const coords = cityCoords[cityId];
    if (!coords) continue;
    const key = coordCacheKey(coords.latitude, coords.longitude);
    let geocoded = geocodeCache.get(key);
    if (geocoded === undefined) {
      try {
        geocoded = await reverseGeocode(coords);
      } catch {
        geocoded = null;
      }
      geocodeCache.set(key, geocoded ?? null);
    }
    if (!geocoded?.countryISO2) continue;
    const meta = cityVisitMeta[cityId] ?? {};
    meta.countryISO2 = geocoded.countryISO2;
    if (geocoded.countryName?.trim()) {
      meta.countryName = geocoded.countryName.trim();
    }
    cityVisitMeta[cityId] = meta;
  }
}

/** Unique city list from ports, preserving order; keeps At Sea segments. */
export function mapPortsToCities(ports: CruisePortStop[]): {
  cityIds: string[];
  cityNames: Record<string, string>;
  cityCoords: Record<string, { latitude: number; longitude: number }>;
  cityVisitMeta: Record<string, TripCityVisitMeta>;
  portLabels: string[];
} {
  const cityIds: string[] = [];
  const cityNames: Record<string, string> = {};
  const cityCoords: Record<string, { latitude: number; longitude: number }> = {};
  const cityVisitMeta: Record<string, TripCityVisitMeta> = {};
  const portLabels: string[] = [];

  for (const stop of ports) {
    const label = displayPortName(stop.port);
    if (!label) continue;
    portLabels.push(label);
    const cityId = crypto.randomUUID();
    cityIds.push(cityId);
    cityNames[cityId] = label;
    const lat = typeof stop.latitude === 'number' ? stop.latitude : Number(stop.latitude);
    const lng = typeof stop.longitude === 'number' ? stop.longitude : Number(stop.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0)) {
      cityCoords[cityId] = { latitude: lat, longitude: lng };
    }
    const { arrivalHm, departureHm } = parsePortTimeParts(stop.time ?? '');
    const atSea =
      isAtSeaLocation(label) || label.toLowerCase().includes('cruising');
    cityVisitMeta[cityId] = {
      arrivalDate: stop.date || null,
      departureDate: stop.date || null,
      portArrivalHm: arrivalHm,
      portDepartureHm: departureHm,
      cruisePortStatus: atSea ? 'cruising' : 'docked',
      aiJobId: null,
      aiJobStartedAt: null,
      aiStatus: null,
      aiUpdatedAt: null,
    };
  }

  return { cityIds, cityNames, cityCoords, cityVisitMeta, portLabels };
}

export async function mapCruiseItineraryToTrip(
  detail: CruiseItineraryDetail,
  options?: {
    tripName?: string;
    daysBeforeCruise?: number;
    daysAfterCruise?: number;
    reverseGeocode?: typeof reverseGeocodeCountry;
  }
): Promise<CruiseImportMappedTrip> {
  const ports = detail.ports ?? [];
  const { cityIds, cityNames, cityCoords, cityVisitMeta, portLabels } = mapPortsToCities(ports);

  await enrichMappedPortsWithCountry({
    ports,
    cityIds,
    cityNames,
    cityCoords,
    cityVisitMeta,
    reverseGeocode: options?.reverseGeocode,
  });

  const daysBefore = clampDays(options?.daysBeforeCruise ?? 0);
  const daysAfter = clampDays(options?.daysAfterCruise ?? 0);

  let startDate = detail.embarkDate;
  let endDate = detail.endDate;

  if (ports.length > 0) {
    const firstDocked = ports.find((p) => !isAtSeaLocation(displayPortName(p.port)));
    const lastDocked = [...ports].reverse().find((p) => !isAtSeaLocation(displayPortName(p.port)));
    startDate = firstDocked?.date || ports[0].date || detail.embarkDate;
    endDate = lastDocked?.date || ports[ports.length - 1].date || detail.endDate;
  }

  if (daysBefore > 0) startDate = addDaysToIsoDate(startDate, -daysBefore);
  if (daysAfter > 0) endDate = addDaysToIsoDate(endDate, daysAfter);

  const trimmedName = options?.tripName?.trim() ?? '';
  // Empty name falls back to traveler start (includes Pre-Cruise) — iOS #146.
  const tripName =
    trimmedName || generatedCruiseTripName(detail.shipName, startDate);

  if (cityIds.length === 0) {
    throw new Error('This cruise has no ports to import.');
  }

  return {
    tripName,
    startDate,
    endDate,
    cityIds,
    cityNames,
    cityCoords,
    cityVisitMeta,
    portLabels,
    daysBeforeCruise: daysBefore,
    daysAfterCruise: daysAfter,
    itineraryId: detail.itineraryId,
    cruiseLine: detail.cruiseLine.trim(),
    shipName: detail.shipName.trim(),
    cruiseType: detail.cruiseType?.trim() || null,
  };
}

export function mappedImportToTripData(mapped: CruiseImportMappedTrip): Omit<Trip, 'id'> {
  const empty = createEmptyTrip();
  return {
    ...empty,
    title: mapped.tripName,
    tripType: 'cruise',
    startDate: mapped.startDate,
    endDate: mapped.endDate,
    cityIds: mapped.cityIds,
    cityNames: mapped.cityNames,
    cityCoords: mapped.cityCoords,
    cityVisitMeta: mapped.cityVisitMeta,
    sourceItineraryId: mapped.itineraryId || null,
    cruiseLine: mapped.cruiseLine || null,
    shipName: mapped.shipName || null,
    cruiseType: mapped.cruiseType?.trim() || null,
  };
}

/** True when two date ranges overlap (inclusive YYYY-MM-DD). */
export function dateRangesOverlap(
  aStart: string | null,
  aEnd: string | null,
  bStart: string | null,
  bEnd: string | null
): boolean {
  const as = aStart ?? aEnd;
  const ae = aEnd ?? aStart;
  const bs = bStart ?? bEnd;
  const be = bEnd ?? bStart;
  if (!as || !ae || !bs || !be) return false;
  return as <= be && bs <= ae;
}
